import * as React from 'react';
import {
  CheckBoxWithLabelWidget,
  TextAreaWidget,
  TextLinkWidget
} from '@tableau/widgets';

import { Messages } from '../../codegen/Localize';
import { characterCounter } from '../content/CharacterCounter';
import {
  checkboxStyle,
  ContentItemNameDiv,
  DATA_QUALITY_DIALOG_HELP_TEST_ID,
  DATA_QUALITY_DIALOG_MESSAGE_ID,
  DATA_QUALITY_DIALOG_TOGGLE_TEST_ID,
  HelpTextDiv,
  InputHintDiv,
  textAreaStyle,
  textAreaWidgetClass
} from '../content/styles/DataQualityStyles';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { ReactActionDialogBodyProps, ReactActionDialogOptions } from '../../commonInfra/dialogs/ReactActionDialog';
import { SevereWarningContent, isTextAreaValid, isFormDataValidForSubmission } from './DataQualityDialog';
import { HelpService } from '~commonInfra/services/HelpService';

const MESSAGE_MAXIMUM_LENGTH: number = 4000;

export const defaultInitialState: DataQualityTriggerDialogState = {
  enableTrigger: false,
  isSevere: false,
  message: '',
};

export interface DataQualityTriggerDialogBodyProps {
  readonly contentName: string;
  readonly contentType: 'datasource' | 'flow';
}

export interface DataQualityTriggerDialogState {
  readonly enableTrigger: boolean;
  readonly isSevere: boolean;
  readonly message: string;
}

function getDialogStrings(contentType: string) {
  if (contentType === 'datasource') {
    return {
      title: Messages.dataQualityTriggerDialog_Title_Datasource(),
      prompt: Messages.dataQualityTriggerDialog_Prompt_Datasource(),
      checkboxLabel: Messages.dataQualityTriggerDialog_EnableTriggerCheckboxLabel_Datasource(),
      helpToken: 'dqw-auto-extract',
    };
  } else {
    return {
      title: Messages.dataQualityTriggerDialog_Title_Flow(),
      prompt: Messages.dataQualityTriggerDialog_Prompt_Flow(),
      checkboxLabel: Messages.dataQualityTriggerDialog_EnableTriggerCheckboxLabel_Flow(),
      helpToken: 'dqw-auto-flow',
    };
  }
}

export type DataQualityTriggerActionDialogOptions = ReactActionDialogOptions<DataQualityTriggerDialogBodyProps, DataQualityTriggerDialogState, unknown>;
export type DataQualityTriggerDialogBodyComponentProps = ReactActionDialogBodyProps<DataQualityTriggerDialogBodyProps, DataQualityTriggerDialogState>;

export function DataQualityTriggerDialogBody(props: DataQualityTriggerDialogBodyComponentProps): React.ReactElement {

  return (
    <React.Fragment>

      <ContentItemNameDiv>{props.contentName}</ContentItemNameDiv>

      <HelpTextDiv>
        {getDialogStrings(props.contentType).prompt}
        <TextLinkWidget
          href={HelpService.getServerHelpUrlByCategory(getDialogStrings(props.contentType).helpToken)}
          testId={DATA_QUALITY_DIALOG_HELP_TEST_ID}
          target="_blank" rel="noopener noreferrer"
        >
          {Messages.dataQuality_learnMore()}
        </TextLinkWidget>
      </HelpTextDiv>

      <CheckBoxWithLabelWidget
        id={DATA_QUALITY_DIALOG_TOGGLE_TEST_ID}
        label={getDialogStrings(props.contentType).checkboxLabel}
        testId={DATA_QUALITY_DIALOG_TOGGLE_TEST_ID}
        checked={props.dialogState.enableTrigger}
        handleChange={(enableTrigger: boolean) => props.updateDialogState({ enableTrigger })}
        removeLeftMargin={true}
        containerStyle={checkboxStyle} />

      <SevereWarningContent
        disabled={!props.dialogState.enableTrigger}
        isSevere={props.dialogState.isSevere}
        showHelpText={false}
        setIsSevere={(isSevere: boolean) => props.updateDialogState({ isSevere })} />

      <TextAreaWidget
        label={<InputHintDiv>{Messages.dataQualityMessage()}</InputHintDiv>}
        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => props.updateDialogState({ message: event.target.value })}
        id={DATA_QUALITY_DIALOG_MESSAGE_ID}
        testId={DATA_QUALITY_DIALOG_MESSAGE_ID}
        disabled={!props.dialogState.enableTrigger}
        className={textAreaWidgetClass}
        valid={isTextAreaValid(props.dialogState) ? undefined : false}
        value={props.dialogState.message}
        style={textAreaStyle}
        message={characterCounter({ value: props.dialogState.message, maximum: MESSAGE_MAXIMUM_LENGTH })} />

    </React.Fragment>
  );
}

export function getDataQualityTriggerActionDialogOptions(args: {
  initialBodyProps: DataQualityTriggerDialogBodyProps;
  initialFormData: DataQualityTriggerDialogState;
  doAction: (formData: DataQualityTriggerDialogState) => Promise<unknown>;
}): DataQualityTriggerActionDialogOptions {

  const { initialBodyProps, initialFormData, doAction } = args;

  return {
    title: getDialogStrings(args.initialBodyProps.contentType).title,
    bodyComponent: DataQualityTriggerDialogBody,
    initialDialogState: initialFormData,
    initialBodyProps: initialBodyProps,
    actionName: Messages.save(),
    action: (_, dialogState) => doAction(dialogState),
    actionDisabled: (_, dialogState) => !isFormDataValidForSubmission({ initialFormData, currentFormData: dialogState }),
    dismissDialogOnActionFailed: true,
    dialogWidth: TabDialogWidth.width500px,
    initiallyFocusedElementSelector: `#${DATA_QUALITY_DIALOG_TOGGLE_TEST_ID}`
  };
}
